import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Info,
  Coins,
  Shield,
  HelpCircle,
  ChevronRight,
  CheckCircle,
  Clock,
  DollarSign,
  Users,
  AlertCircle,
  Lock,
  Eye,
  FileText
} from 'lucide-react';
import PortalHeader from '../components/PortalHeader';

const HowItWorks = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['overview', 'stablecoins', 'security', 'faq'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Info },
    { id: 'stablecoins', label: 'Stablecoins', icon: Coins },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'faq', label: 'FAQ', icon: HelpCircle },
  ];

  const OverviewContent = () => (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
          How Koin Remit works
        </h1>
        <p className="text-lg text-gray-600">
          Your family sends a request. You pay. The money is on their phone in minutes.
        </p>
      </div>

      {/* What is Koin Remit */}
      <div className="bg-blue-50 rounded p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">What is Koin Remit?</h2>
        <p className="text-gray-700 leading-relaxed">
          Koin Remit is a money transfer service built into feature phones. It lets your family in India (or other countries) send you a payment request directly from their phone — with the exact amount, their name, and their address pre-filled. You receive the request by email or WhatsApp, open it, pay with your card or bank account, and the money arrives on their phone automatically.
        </p>
        <p className="text-gray-700 mt-4 font-medium">
          No trip to a Western Union branch. No remittance kiosk. No cash.
        </p>
      </div>

      {/* How it works - step by step */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-6">How it works — step by step</h2>
        <div className="space-y-4">
          {[
            {
              step: 1,
              title: "Your family makes a request",
              description: "On their phone, your family selects the amount they need and sends a request. This takes about 30 seconds.",
              icon: Users
            },
            {
              step: 2,
              title: "You receive a notification",
              description: "You get an email or WhatsApp message with a secure link to this page. The request shows your family's verified name, address, date of birth, and the exact amount.",
              icon: AlertCircle
            },
            {
              step: 3,
              title: "You review and confirm",
              description: "You check the details on this screen. If everything looks right, you tap Continue. You are never charged until you explicitly confirm.",
              icon: FileText
            },
            {
              step: 4,
              title: "You pay with your card or bank",
              description: "You pay in your local currency (USD, EUR, GBP, AED, etc.). Koin Remit handles the international conversion automatically.",
              icon: DollarSign
            },
            {
              step: 5,
              title: "Your family receives the money",
              description: "Within minutes, the balance appears on their phone. They can cash out to their UPI account, bank account, or mobile money wallet directly from the phone — no smartphone needed.",
              icon: CheckCircle
            }
          ].map((item) => (
            <div key={item.step} className="flex items-start space-x-4 bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-primary-600 font-bold">{item.step}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <item.icon className="w-5 h-5 text-primary-600" />
                  <h3 className="font-semibold text-gray-900">{item.title}</h3>
                </div>
                <p className="text-gray-600 text-sm">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* What does it cost */}
      <div className="bg-green-50 rounded p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">What does it cost?</h2>
        <ul className="space-y-3 text-gray-700">
          <li className="flex items-start space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <span>You will always see the exact fee and exchange rate before you confirm. There are no hidden charges added after payment.</span>
          </li>
          <li className="flex items-start space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <span>What you see is what your family receives.</span>
          </li>
          <li className="flex items-start space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <span>Fees are typically 1–2% of the transfer amount — significantly less than traditional remittance services, which often charge 3–8% plus exchange rate markups.</span>
          </li>
        </ul>
      </div>

      {/* Who can use it */}
      <div className="bg-purple-50 rounded p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Who can use it?</h2>
        <p className="text-gray-700 mb-4">
          Koin Remit is designed for people sending money to family members in India, with more countries coming soon.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">The sender (you)</h4>
            <p className="text-sm text-gray-600">Needs a smartphone or computer and a payment card or bank account.</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2">The receiver</h4>
            <p className="text-sm text-gray-600">Only needs a feature phone — no smartphone, no bank account, no app to download.</p>
          </div>
        </div>
      </div>
    </div>
  );

  const StablecoinsContent = () => (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
          What is a stablecoin, and why do we use it?
        </h1>
      </div>

      {/* Short Answer */}
      <div className="bg-green-50 border-l-4 border-green-500 rounded-r-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">The short answer</h2>
        <p className="text-gray-700">
          Your family receives Indian rupees (₹ INR), not cryptocurrency. The stablecoin is the invisible rail we use to move your money across borders — fast, cheap, and fully regulated. <strong>You do not need to understand crypto to use Koin Remit.</strong>
        </p>
      </div>

      {/* What is a stablecoin */}
      <div className="bg-white border border-gray-200 rounded p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">What is a stablecoin?</h2>
        <p className="text-gray-700 mb-4">
          A stablecoin is a digital currency whose value is always equal to a real currency. Koin Remit uses USDT (Tether), where <strong>1 USDT = 1 US Dollar — permanently</strong>.
        </p>
        <p className="text-gray-700 mb-4">
          Unlike Bitcoin or other cryptocurrencies, stablecoins do not fluctuate in value. There is no speculation, no price risk, and no exchange rate surprise.
        </p>
        <div className="bg-blue-50 rounded-lg p-4 mt-4">
          <p className="text-gray-700 italic">
            Think of it this way: if you put $60 in on one side, the equivalent of $60 comes out the other side — in local currency, at the current exchange rate — every time.
          </p>
        </div>
      </div>

      {/* Why use it instead of bank transfer */}
      <div className="bg-orange-50 rounded p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Why do we use it instead of a normal bank transfer?</h2>
        <p className="text-gray-700 mb-4">Traditional international wire transfers have three major problems:</p>
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <Clock className="w-6 h-6 text-orange-600 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900">Speed</h4>
              <p className="text-gray-600 text-sm">A wire transfer from the UK or US to India typically takes 2 to 5 business days. Stablecoin transfers settle on a blockchain in under 5 minutes, regardless of time zones, bank holidays, or weekends.</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <DollarSign className="w-6 h-6 text-orange-600 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900">Cost</h4>
              <p className="text-gray-600 text-sm">Wire transfers often cost $15–$30 in flat fees, plus the bank applies its own exchange rate markup — often 2–4% above the mid-market rate. Stablecoin infrastructure operates at a fraction of that cost, and we pass the savings on.</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Users className="w-6 h-6 text-orange-600 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-gray-900">Availability</h4>
              <p className="text-gray-600 text-sm">Traditional transfers require both parties to have bank accounts. Your family member's phone has a built-in wallet that receives the stablecoin automatically — no bank account required on the receiving end.</p>
            </div>
          </div>
        </div>
      </div>

      {/* What does your family receive */}
      <div className="bg-white border border-gray-200 rounded p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">What does your family actually receive?</h2>
        <p className="text-gray-700 mb-4">Your family never sees or touches stablecoins. The process is:</p>
        <div className="space-y-3">
          {[
            "You pay in your local currency (USD, GBP, EUR, AED).",
            "A licensed payment partner converts it to USDT and credits your sending wallet.",
            "USDT travels from your wallet to your family member's phone wallet on the Polygon blockchain — this takes 1–3 minutes.",
            "Your family taps \"Cash Out\" on their phone.",
            "A licensed local partner converts the USDT to ₹ INR and pays out to their UPI account or bank account — usually within minutes."
          ].map((step, index) => (
            <div key={index} className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center">
                <span className="text-primary-600 text-sm font-medium">{index + 1}</span>
              </div>
              <p className="text-gray-700">{step}</p>
            </div>
          ))}
        </div>
        <div className="bg-green-50 rounded-lg p-4 mt-4">
          <p className="text-gray-700">
            <strong>The crypto layer is completely invisible to your family.</strong> They see a balance in rupees and a cash-out button.
          </p>
        </div>
      </div>

      {/* What is USDT */}
      <div className="bg-blue-50 rounded p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">What is USDT (Tether)?</h2>
        <ul className="space-y-3 text-gray-700">
          <li className="flex items-start space-x-2">
            <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <span>USDT is the world's most widely used stablecoin, with over $100 billion in circulation.</span>
          </li>
          <li className="flex items-start space-x-2">
            <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <span>It is issued by Tether Ltd., backed 1:1 by US dollar reserves held in regulated financial institutions, and audited quarterly by independent third parties.</span>
          </li>
          <li className="flex items-start space-x-2">
            <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <span>USDT operates on multiple blockchains. Koin Remit uses the Polygon network, which offers fast, low-cost settlement (typically under $0.01 per transaction) with the security guarantees of Ethereum.</span>
          </li>
        </ul>
      </div>

      {/* Is this real money */}
      <div className="bg-green-50 rounded p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Is this real money?</h2>
        <p className="text-gray-700 mb-4">
          <strong>Yes.</strong> USDT is recognized as a regulated digital asset in the EU (under MiCA), the UK (FCA registered), the UAE (VARA licensed), and multiple other jurisdictions.
        </p>
        <p className="text-gray-700">
          Every conversion — from your local currency into USDT, and from USDT into INR — is handled by licensed financial institutions subject to the same regulatory oversight as any bank or money service business.
        </p>
      </div>
    </div>
  );

  const SecurityContent = () => (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
          Your money and your data are safe
        </h1>
      </div>

      {/* Core Guarantee */}
      <div className="bg-green-50 border-l-4 border-green-500 rounded-r-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">The core guarantee</h2>
        <p className="text-gray-700">
          <strong>Koin Remit never holds your money or your family's money.</strong> The moment you pay, your funds move automatically through a chain of licensed, regulated partners until they arrive in your family's wallet. We are the technology layer — not the vault.
        </p>
      </div>

      {/* Who handles your payment */}
      <div className="bg-white border border-gray-200 rounded p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Who handles your payment?</h2>
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">Your card payment — Transak</h4>
            <p className="text-gray-700 text-sm mb-3">
              Your payment is collected by Transak, one of the world's leading regulated crypto on-ramp services. Transak is registered as a Money Services Business (MSB) in the US and Canada, licensed by the UK's Financial Conduct Authority (FCA), and operates under regulatory frameworks in the EU, UAE, and other jurisdictions.
            </p>
            <p className="text-gray-700 text-sm">
              When you enter your card details, you are interacting directly with Transak's secure payment infrastructure — not with Koin Remit. Koin Remit never sees or stores your card number.
            </p>
          </div>
        </div>
      </div>

      {/* How is your family's wallet protected */}
      <div className="bg-purple-50 rounded p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">How is your family's wallet protected?</h2>
        <p className="text-gray-700 mb-4">
          Your family's wallet on their phone is a non-custodial MPC (Multi-Party Computation) wallet. This means:
        </p>
        <ul className="space-y-3 text-gray-700">
          <li className="flex items-start space-x-2">
            <Lock className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <span>The wallet's private keys are mathematically split — no single party, including Kivi3, holds a complete key.</span>
          </li>
          <li className="flex items-start space-x-2">
            <Lock className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <span>Only your family's phone can authorise a transaction from that wallet.</span>
          </li>
          <li className="flex items-start space-x-2">
            <Lock className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <span>Koin Remit cannot access, freeze, or move funds from your family's wallet under any circumstances.</span>
          </li>
          <li className="flex items-start space-x-2">
            <Lock className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <span>The wallet is bound to the phone at the factory level and verified cryptographically — it cannot be spoofed or cloned.</span>
          </li>
        </ul>
      </div>

      {/* Identity Verification */}
      <div className="bg-white border border-gray-200 rounded p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">What about identity verification (KYC)?</h2>
        <p className="text-gray-700 mb-4">
          Both you and your family are identity-verified before money moves. This is a legal requirement in every country, and it exists to protect both of you.
        </p>
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">For you (the sender)</h4>
            <p className="text-gray-700 text-sm">
              Transak collects your name, address, date of birth, and a government-issued ID as part of their licensed onboarding process. This data is held by Transak under GDPR (if you are in the EU or UK) or equivalent local data protection law. <strong>Koin Remit does not store your KYC documents.</strong>
            </p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">For your family (the receiver)</h4>
            <p className="text-gray-700 text-sm">
              KYC is collected by the local off-ramp partner at the cash-out stage, in compliance with Indian regulations.
            </p>
          </div>
        </div>
        <div className="bg-yellow-50 rounded-lg p-4 mt-4">
          <p className="text-gray-700 text-sm">
            The receiver's full name, date of birth, phone number, and address that you see on the Payment Request screen are verified by Koin Remit's backend before the request is sent to you. <strong>This prevents a scammer from impersonating your family member.</strong>
          </p>
        </div>
      </div>

      {/* Fraud Protection */}
      <div className="bg-red-50 rounded p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">What protects you from fraud?</h2>
        <ul className="space-y-3 text-gray-700">
          <li className="flex items-start space-x-2">
            <Shield className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <span>Every payment request is cryptographically linked to a verified receiver wallet. A scammer cannot generate a payment request in your family member's name without passing full identity verification.</span>
          </li>
          <li className="flex items-start space-x-2">
            <Shield className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <span>The receiver's identity data you see on this screen — name, date of birth, phone number, address — is verified and cannot be spoofed.</span>
          </li>
          <li className="flex items-start space-x-2">
            <Shield className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <span>Every transaction is traced on-chain with a permanent, public record. This means that if anything ever goes wrong, the funds can always be located, and the cause of any failure can be identified precisely.</span>
          </li>
        </ul>
      </div>

      {/* Privacy and Data */}
      <div className="bg-gray-50 rounded p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Privacy and data</h2>
        <ul className="space-y-3 text-gray-700">
          <li className="flex items-start space-x-2">
            <Eye className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <span>Your payment card details are processed directly by Transak and are never stored on Koin Remit's servers.</span>
          </li>
          <li className="flex items-start space-x-2">
            <Lock className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <span>All data in transit between your browser and Koin Remit's backend is encrypted using TLS 1.3.</span>
          </li>
          <li className="flex items-start space-x-2">
            <FileText className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <span>Travel Rule compliance data (your name, address, and transaction amount) is transmitted between licensed partners over encrypted bilateral APIs — it is never stored in plaintext or shared with any party outside the regulated payment chain.</span>
          </li>
          <li className="flex items-start space-x-2">
            <Shield className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <span>Koin Remit does not sell data to advertisers and does not use your transaction data for any purpose outside regulatory compliance.</span>
          </li>
        </ul>
      </div>
    </div>
  );

  const FAQContent = () => (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="text-center mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
          Frequently asked questions
        </h1>
      </div>

      {/* FAQ Items */}
      <div className="space-y-4">
        {[
          {
            question: "Can I cancel a payment after I tap Continue?",
            answer: "Once you complete the payment (card charged, funds moved), the transaction is final — just like any bank transfer or wire. If you have not yet tapped Continue on the payment screen, you can cancel at any time using the Cancel button on this page. If you believe a payment was made by mistake or without your authorisation, contact Koin Remit support immediately with your transaction ID. The funds are always traceable."
          },
          {
            question: "What if my family doesn't receive the money?",
            answer: "This is extremely rare, but if it happens: First, check the transaction status on your Transaction History page. If the status shows \"completed\" but your family has not received the funds, contact Koin Remit support with your transaction ID. Every transaction is fully traceable on the blockchain, and we can identify exactly where the funds are at any moment. If there was a failure at any step, the funds are either returned to your wallet or held securely pending resolution."
          },
          {
            question: "Why do I need to verify my identity?",
            answer: "Identity verification (KYC) is a legal requirement for all money transfer services in the UK, EU, US, UAE, and most other jurisdictions. It is mandated by anti-money laundering (AML) regulations and is designed to prevent fraud, money laundering, and terrorist financing. This is not unique to Koin Remit — it applies to Western Union, bank wires, and every other regulated money transfer service. Identity verification also protects you: a verified system ensures that the person requesting money is actually your family member, not someone impersonating them."
          },
          {
            question: "Does my family need to understand cryptocurrency?",
            answer: "No. Your family interacts only with the phone app, which shows their balance in local currency (₹ INR) and a simple Cash Out button. There is no mention of stablecoins, wallets, or blockchain on their screen. The entire crypto infrastructure is invisible to the receiver. This is intentional: crypto is the pipe, not the product."
          },
          {
            question: "What currencies can I pay in?",
            answer: "You can pay in USD, EUR, GBP, AED, CAD, and a growing list of local currencies depending on your country of residence. The currency options available to you are shown at checkout. You always see the exact exchange rate and fee before you confirm."
          },
          {
            question: "How long does the transfer take?",
            answer: "From the moment you complete payment to the moment your family's phone shows the updated balance: typically 3 to 8 minutes under normal conditions. The on-chain settlement on the Polygon network takes 1–3 minutes; the off-ramp payout to UPI takes an additional 2–5 minutes. You and your family both receive a notification when each stage completes."
          },
          {
            question: "What happens if my family's phone is lost or broken?",
            answer: "Your family's wallet is linked to their phone's hardware and their identity — not to a password they can forget. If the phone is replaced, Koin Remit's support team can assist with wallet recovery using the identity verification already on file. Funds in the wallet at the time of loss are safe and cannot be accessed by anyone who finds or steals the phone."
          },
          {
            question: "Is there a minimum or maximum transfer amount?",
            answer: "Minimum and maximum amounts depend on the corridor and the payment method you use, and are set by our licensed on-ramp partner (Transak) in compliance with local regulations. You will see the applicable limits at checkout before entering any payment details."
          },
          {
            question: "Who is Koin Remit / Kivi3?",
            answer: "Koin Remit is the consumer brand of Kivi3 Technologies Ltd, a UAE-incorporated fintech company specialising in stablecoin-based remittance infrastructure. Kivi3 operates under a UAE commercial licence and works exclusively with licensed on-ramp and off-ramp partners in each market. Kivi3 does not hold a custodial licence and does not hold user funds — all regulated activities are performed by our licensed partner network."
          }
        ].map((faq, index) => (
          <details key={index} className="bg-white border border-gray-200 rounded-lg group">
            <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
              <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
              <ChevronRight className="w-5 h-5 text-gray-500 transform transition-transform group-open:rotate-90 flex-shrink-0" />
            </summary>
            <div className="px-4 pb-4 text-gray-700 text-sm border-t border-gray-100 pt-4">
              {faq.answer}
            </div>
          </details>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <OverviewContent />;
      case 'stablecoins':
        return <StablecoinsContent />;
      case 'security':
        return <SecurityContent />;
      case 'faq':
        return <FAQContent />;
      default:
        return <OverviewContent />;
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <PortalHeader onBack={handleBackClick} backLabel="Back" showHome />
      <main className="mx-auto max-w-5xl px-0 py-5 sm:px-4 sm:py-8">
        <div>
        {/* Tab Navigation */}
        <div className="border-y border-slate-200 bg-white">
            <nav className="grid grid-cols-4" aria-label="How Koin Remit works sections">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex min-h-14 min-w-0 items-center justify-center border-b-2 px-1 text-xs font-medium transition-colors sm:px-6 sm:text-sm ${
                    activeTab === tab.id
                      ? 'border-cyan-700 text-cyan-800'
                      : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-800'
                  }`}
                >
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
        </div>

        {/* Content */}
        <div className="bg-white px-4 py-6 sm:px-8">
          {renderContent()}
        </div>

        {/* Footer */}
        <p className="border-y border-slate-100 bg-white px-4 py-3 text-center text-[10px] text-slate-400">
          Secured by Koin Remit · Travel Rule Compliant
        </p>
        </div>
      </main>
    </div>
  );
};

export default HowItWorks;
